/**
 * @fileoverview Time Limit Driver - 時間制限ドライバ（Blitz モード）
 * RAF は使用せず、presentation層から update(nowMs) が呼ばれる
 *
 * ## 状態管理
 * 単一フェーズフィールドで状態を表現:
 * - LIMIT_RUNTIME_PHASE: 'idle' | 'intro' | 'running' | 'consumed'
 *
 * ## 状態遷移
 * idle (Title画面)
 *   ↓ start()
 * intro (イントロアニメーション中)
 *   ↓ update() でイントロ完了
 * running (タイマー実行中)
 *   ↓ update() で時間切れ検出
 * consumed (制限到達、GameOver待ち)
 *   ↓ stop()
 * idle (Title画面に戻る)
 */

import { GameStatus } from '../../core/constants.js';
import { visibilityManager } from '../../platform/visibility-manager.js';
import { DEFAULT_LIMIT_VALUES, INTRO_ANIMATION } from '../../shared/constants/limit-defaults.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';

import { createInterpolator, easings } from './interpolator.js';
import * as limitSuspension from './limit-suspension.js';

let introInterpolator = null;
let introCompleteCallback = null;

/** タイマー実行中の前回tick時刻（ms）。0=停止中 */
let localLastTickMs = 0;

/** 累積経過時間（ms）。タイマー実行中のみ増加 */
let localTotalActiveMs = 0;

/** 最後にログした秒数（秒変化判定用） */
let localLastLoggedSecond = -1;

/**
 * 次回delta時間を無視するフラグ
 * true時: 次回update()でdelta加算をスキップ
 * 用途:
 * - ゲーム一時停止後のresume時、pause中の経過時間を除外
 * - 制限到達後の余分な時間加算を防止
 */
let localSkipNextDelta = true;

/**
 * Delta時間の最大許容値（ms）
 * タブ切り替え等で巨大なdeltaが発生した場合の上限
 * 200ms超過分は切り捨てる（不正な時間ジャンプ防止）
 */
const MAX_DELTA_MS = 200;

export function configure(gameState, options) {
  const ms = Math.max(1000, Number(options.timeLimitMs) || 0);
  gameState.setState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS, ms);
}

/**
 * 最終集計（内部専用）
 * stop()の前に呼ばれ、最後のフレームの経過時間を集計する
 */
function finalize(gameState) {
  const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);
  if (phase === 'running' && localLastTickMs !== 0) {
    const shouldAccumulate = gameState.getState(STATE_PATHS.CORE_STATUS) === GameStatus.Ready;
    if (shouldAccumulate) {
      const deltaRaw = performance.now() - localLastTickMs;
      const delta = Math.min(deltaRaw, MAX_DELTA_MS);
      localTotalActiveMs += delta;
    }
  }
}

export function start(gameState, onIntroComplete = null) {
  // 冪等性保証: 既存状態をクリア（reset統合）
  localLastTickMs = 0;
  localTotalActiveMs = 0;
  localLastLoggedSecond = -1;
  localSkipNextDelta = true;
  limitSuspension.reset();

  if (introInterpolator) {
    introInterpolator.stop();
    introInterpolator = null;
  }
  introCompleteCallback = null;

  // 初期値設定（最大値を残り値にセット、経過時間はゼロ）
  const timeLimitMs =
    gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS) ||
    DEFAULT_LIMIT_VALUES.TIME_LIMIT_MS;
  const initialRemainingSecs = Math.ceil(timeLimitMs / 1000);
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, initialRemainingSecs);
  localLastLoggedSecond = initialRemainingSecs;
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_TOTAL_ACTIVE_MS, 0);

  // イントロフェーズへ遷移
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'intro');
  introCompleteCallback = onIntroComplete;

  introInterpolator = createInterpolator({
    from: 0,
    to: 1,
    durationMs: INTRO_ANIMATION.DURATION_MS,
    easingFn: easings.cubicOut,
  });

  introInterpolator.start(performance.now());
}

export function stop(gameState) {
  // 1. 最終集計
  finalize(gameState);

  // 2. 停止処理
  if (introInterpolator) {
    introInterpolator.stop();
  }

  // 3. idle状態へ遷移
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'idle');

  // 4. ゼロ化（Title状態用）
  // 経過時間を最大値として保存（残り=0を表現）
  const timeLimitMs = gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS) || 0;
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_TOTAL_ACTIVE_MS, timeLimitMs);

  // 外部停止要因をクリア
  limitSuspension.reset();
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, 0);

  localLastTickMs = 0;
  localTotalActiveMs = timeLimitMs;
  localSkipNextDelta = true;
}

/**
 * イントロアニメーション更新（内部関数）
 */
function updateIntroAnimation(gameState, nowMs) {
  const continuing = introInterpolator.update(nowMs);
  if (!continuing) {
    // イントロ完了 → runningフェーズへ遷移
    gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'running');

    localLastTickMs = nowMs;
    localSkipNextDelta = false;

    if (typeof introCompleteCallback === 'function') {
      introCompleteCallback();
      introCompleteCallback = null;
    }
  }
}

/**
 * タイマーカウントダウン更新（内部関数）
 */
function updateTimerCountdown(gameState, nowMs) {
  const timeLimitMs = gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS) || 0;

  // Delta時間計算（上限200ms）
  const deltaRaw = Math.max(0, nowMs - localLastTickMs);
  const delta = Math.min(deltaRaw, MAX_DELTA_MS);
  localLastTickMs = nowMs;

  // ゲーム一時停止中は時間加算しない
  const shouldAccumulate =
    gameState.getState(STATE_PATHS.CORE_STATUS) === GameStatus.Ready &&
    !limitSuspension.isSuspended();

  if (shouldAccumulate) {
    accumulateTime(gameState, delta, timeLimitMs);
  } else {
    // 一時停止中: 次回resumeで最初のdeltaをスキップ
    localSkipNextDelta = true;
  }
}

/**
 * 時間加算と制限チェック（内部関数）
 */
function accumulateTime(gameState, delta, timeLimitMs) {
  // Resume直後の最初のdelta無視（pause中の経過時間除外）
  if (localSkipNextDelta) {
    localSkipNextDelta = false;
    return;
  }

  // 経過時間加算
  const newTotalActiveMs = localTotalActiveMs + delta;
  const remainingMs = Math.max(0, timeLimitMs - newTotalActiveMs);
  const newRemainingSecs = Math.ceil(remainingMs / 1000);

  localTotalActiveMs = newTotalActiveMs;

  // GameState更新（秒が変わった時のみ）
  if (newRemainingSecs !== localLastLoggedSecond) {
    gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, newRemainingSecs);
    localLastLoggedSecond = newRemainingSecs;
  }

  // 制限到達チェック
  if (remainingMs <= 0) {
    handleLimitReached(gameState);
  }
}

/**
 * 制限到達時の処理（内部関数）
 */
function handleLimitReached(gameState) {
  const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);
  if (phase === 'consumed') return; // 既に処理済み

  // consumedフェーズへ遷移
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'consumed');
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, 0);

  localSkipNextDelta = true;

  // コールバック発火
  const callback = gameState.getState(STATE_PATHS.LIMIT_ON_LIMIT_REACHED);
  if (typeof callback === 'function') {
    callback();
  }
}

export function update(gameState, nowMs) {
  // イントロ処理
  if (introInterpolator && introInterpolator.isRunning()) {
    updateIntroAnimation(gameState, nowMs);
    return;
  }

  // タイマー本体
  const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);
  if (phase !== 'running') return;

  updateTimerCountdown(gameState, nowMs);
}

/**
 * 残り時間をミリ秒単位で計算（内部ヘルパー）
 * 実行中・停止中を問わず統一的に計算
 */
function getRemainingMs(gameState) {
  const timeLimitMs = gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS) || 0;
  const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);

  if (phase === 'running' && localLastTickMs !== 0) {
    // 実行中: ローカル変数から計算（リアルタイム）
    const currentTime = localTotalActiveMs + (performance.now() - localLastTickMs);
    return Math.max(0, timeLimitMs - currentTime);
  }

  // 停止中: GameStateから経過時間を読む（ms単位）
  const totalActiveMs = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_TOTAL_ACTIVE_MS) || 0;
  return Math.max(0, timeLimitMs - totalActiveMs);
}

export function getRemainingValue(gameState) {
  return Math.ceil(getRemainingMs(gameState) / 1000);
}

export function getProgressRatio(gameState) {
  const timeLimitMs = gameState.getState(STATE_PATHS.LIMIT_CONFIG_TIME_LIMIT_MS) || 0;
  if (timeLimitMs === 0) return 0;

  const remainingMs = getRemainingMs(gameState);
  return Math.max(0, Math.min(1, remainingMs / timeLimitMs));
}

export function getType() {
  return 'time';
}

export function getDisplayLabel() {
  return 'seconds';
}

export function getIntroProgress() {
  return introInterpolator ? introInterpolator.getProgress() : 0;
}

export function isIntroAnimating(gameState) {
  return gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE) === 'intro';
}

export function initializeVisibilityHandling() {
  visibilityManager.onResumed(() => {
    if (localLastTickMs !== 0) {
      localLastTickMs = performance.now();
      localSkipNextDelta = true;
    }
  });
}
